import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import BaseInput from "components/BaseInput";
import {
  useChangeClientPhoneMutation,
  useChangeClientPhoneWithCodeMutation,
  useGetClientProfileQuery,
  useGetStreetsQuery,
  useUpdateClientProfileMutation,
} from "store/api";
import { useFormik } from "formik";
import { IFormValues } from "components/BaseInput/types";
import { initialValues } from "./constants";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import { findErrorById } from "config/constants";
import { signIn } from "store/slices/auth";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { useAppSelector } from "store/hooks/useAppSelector";
import SearchAddressInput from "pages/OrderModal/components/SearchAddressInput";
import {
  IFoundPlaces,
  IHouse,
} from "pages/OrderModal/components/SearchAddressInput/types";
import SearchBuildingInput from "pages/OrderModal/components/SearchBuildingInput";
import { shallowEqual } from "react-redux";
import orderStyles from "pages/OrderModal/OrderModal.styled";
import styles from "./ClientSettings.styled";

const ClientSettings = () => {
  const [isCodeSend, setIsCodeSend] = useState<boolean>(false);
  const { data: clientData, isSuccess, refetch } = useGetClientProfileQuery();
  const [changeClientPhone] = useChangeClientPhoneMutation();
  const [changeClientPhoneWithCode] = useChangeClientPhoneWithCodeMutation();
  const [updateClientProfile] = useUpdateClientProfileMutation();
  const [searchAddressValue, setSearchAddressValue] = useState<string>("");
  const [foundPlaces, setFoundPlaces] = useState<IFoundPlaces[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<IFoundPlaces | null>(
    null,
  );
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const accentColor = useAppSelector(
    (state) => state.settings.config.accentColor,
    shallowEqual,
  );
  const prevToken = useRef(token);
  const { data, isLoading } = useGetStreetsQuery(
    { q: searchAddressValue },
    {
      skip: searchAddressValue.length < 3,
    },
  );

  const formik = useFormik<IFormValues>({
    initialValues,
    onSubmit: async (values) => {
      const dataToSend = {
        ["user_first_name"]: values.user_first_name,
        ["user_middle_name"]: values.user_middle_name,
        ["user_last_name"]: values.user_last_name,
        ["route_address_from"]: values.route_address_from,
        ["route_address_number_from"]: values.route_address_number_from,
        ["route_address_entrance_from"]: Number(
          values.route_address_entrance_from,
        ),
        ["route_address_apartment_from"]: Number(
          values.route_address_apartment_from,
        ),
      };
      try {
        await updateClientProfile(dataToSend).unwrap();
        useNotificationToast({
          type: "success",
          message: "Дані успішно оновлені",
        });
      } catch (error) {
        useNotificationToast({
          type: error,
        });
      }
    },
  });

  useEffect(() => {
    if (clientData && isSuccess) {
      formik.setFieldValue("user_first_name", clientData.user_first_name);
      formik.setFieldValue("user_middle_name", clientData.user_middle_name);
      formik.setFieldValue("user_last_name", clientData.user_last_name);
      formik.setFieldValue("user_phone", clientData.user_phone);
      formik.setFieldValue("route_address_from", clientData.route_address_from);
      formik.setFieldValue(
        "route_address_number_from",
        clientData.route_address_number_from,
      );
      formik.setFieldValue(
        "route_address_entrance_from",
        clientData.route_address_entrance_from,
      );
      formik.setFieldValue(
        "route_address_apartment_from",
        clientData.route_address_apartment_from,
      );
    }
  }, [clientData, isSuccess]);

  useEffect(() => {
    if (prevToken.current && prevToken.current !== token) {
      refetch();
    }
    prevToken.current = token;
  }, [token, refetch]);

  useEffect(() => {
    if (data?.geo_street.length) {
      setFoundPlaces(data.geo_street);
    }
  }, [data]);

  const handleCodeSend = () => {
    const fetchData = async () => {
      try {
        if (isCodeSend) {
          const token = await changeClientPhoneWithCode({
            phone: formik.values.user_phone.slice(1),
            ["confirm_code"]: formik.values.confirm_code,
          }).unwrap();
          dispatch(signIn({ token: token.AccessToken }));
          setIsCodeSend(false);
          await formik.setFieldValue("confirm_code", "");
          useNotificationToast({
            type: "success",
            message: "Телефон успішно оновлений",
          });
        } else {
          await changeClientPhone(formik.values.user_phone.slice(1)).unwrap();
          setIsCodeSend(true);
        }
      } catch (error) {
        setIsCodeSend(false);
        await formik.setFieldValue("confirm_code", "");
        useNotificationToast({
          type: "error",
          message: findErrorById(error.data.Id),
        });
      }
    };

    fetchData();
  };

  const handleAddressSearch = (newSearchValue: string) => {
    if (newSearchValue) {
      setSearchAddressValue(newSearchValue);
    } else {
      setSearchAddressValue("");
      formik.setFieldValue("route_address_from", "");
    }
  };

  const handleBuildingSearch = (newSearchValue: string) => {
    if (!newSearchValue) {
      formik.setFieldValue("route_address_number_from", "");
    }
  };

  const handleSelectedAddress = (value: IFoundPlaces | null) => {
    if (value) {
      setSelectedAddress(value);
      formik.setFieldValue("route_address_from", value.name);
    }
  };

  const handleSelectedBuilding = (value: IHouse | null) => {
    if (value) {
      formik.setFieldValue("route_address_number_from", value.house);
    }
  };

  return (
    <>
      {isSuccess && (
        <form onSubmit={formik.handleSubmit}>
          <Box sx={styles.contentWrap}>
            <Box sx={styles.infoWrap}>
              <Typography sx={styles.title}>Персональні дані</Typography>
              <BaseInput
                id="user_first_name"
                name="user_first_name"
                type="text"
                label="Прізвище"
                formik={formik}
                initialValue={clientData.user_first_name}
              />
              <BaseInput
                id="user_middle_name"
                name="user_middle_name"
                type="text"
                label="Імʼя"
                formik={formik}
                initialValue={clientData.user_middle_name}
              />
              <BaseInput
                id="user_last_name"
                name="user_last_name"
                type="text"
                label="По батькові"
                formik={formik}
                initialValue={clientData.user_last_name}
              />
              <Button
                type="submit"
                sx={{ ...orderStyles.btn(accentColor), ...styles.btn }}
              >
                Оновити
              </Button>
              <BaseInput
                id="user_phone"
                name="user_phone"
                type="text"
                label="Телефон"
                formik={formik}
                initialValue={formik.values.user_phone}
              />
              <BaseInput
                id="confirm_code"
                label="Код"
                name="confirm_code"
                type="text"
                formik={formik}
                sx={styles.code}
                initialValue={formik.values.confirm_code}
                disabled={!isCodeSend}
              />
              {isCodeSend && (
                <Typography sx={styles.codeText}>
                  На вказаний номер телефону був відправлений код підтвердження.
                  Будь ласка, введіть його у відповідну графу
                </Typography>
              )}
              <Button
                type="button"
                sx={{ ...orderStyles.btn(accentColor), ...styles.btn }}
                onClick={handleCodeSend}
              >
                {`${isCodeSend ? "Оновити" : "Отримати код і оновити"}`}
              </Button>
            </Box>
            <Box sx={styles.infoWrap}>
              <Typography sx={styles.title}>Адреса</Typography>
              <Box sx={styles.srcAddressWrap}>
                <SearchAddressInput
                  name="route_address_from"
                  initialValue={formik.values.route_address_from}
                  data={foundPlaces}
                  onSearch={handleAddressSearch}
                  label="Вулиця"
                  onSelectValue={handleSelectedAddress}
                />
                {isLoading && (
                  <CircularProgress
                    color="inherit"
                    size="24px"
                    sx={styles.progress}
                  />
                )}
                <SearchBuildingInput
                  name="route_address_number_from"
                  label="Будинок"
                  initialValue={formik.values.route_address_number_from}
                  data={selectedAddress?.houses || []}
                  onSelectValue={handleSelectedBuilding}
                  onSearch={handleBuildingSearch}
                />
              </Box>
              <BaseInput
                id="route_address_entrance_from"
                name="route_address_entrance_from"
                type="text"
                label="Підʼїзд"
                formik={formik}
                initialValue={
                  clientData.route_address_entrance_from
                    ? clientData.route_address_entrance_from.toString()
                    : ""
                }
              />
              <BaseInput
                id="route_address_apartment_from"
                name="route_address_apartment_from"
                type="text"
                label="Квартира"
                formik={formik}
                initialValue={
                  clientData.route_address_apartment_from
                    ? clientData.route_address_apartment_from.toString()
                    : ""
                }
              />
              <Button
                type="submit"
                sx={{ ...orderStyles.btn(accentColor), ...styles.btn }}
              >
                Оновити
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </>
  );
};

export default ClientSettings;
